/**
 * Shared timer stop logic used by TaskTimer, ActiveTasksWidget and TimeTrackingWidget.
 *
 * Why a shared utility?
 * ─────────────────────
 * Three different components can stop a running timer.  Previously they each
 * did it differently — only TaskTimer created a time_entry record, which meant:
 *   • Time stopped from the dashboard was silently lost.
 *   • horas_ejecutadas (maintained by a DB trigger that sums time_entries) was
 *     never updated when stopping from the dashboard.
 *
 * What this utility does
 * ──────────────────────
 * 1. RECONCILE MANUAL HOURS
 *    horas_ejecutadas can be edited directly in TaskModal without creating a
 *    time_entry.  When the DB trigger later recalculates
 *    horas_ejecutadas = SUM(time_entries) it would overwrite those manual hours
 *    with a lower (or zero) value.
 *    Fix: before inserting the new time_entry we check if
 *    task.horas_ejecutadas > SUM(existing time_entries).  If so, the gap
 *    represents manually-entered work — we back-fill it as a synthetic
 *    is_manual=true time_entry placed just before this timer session starts.
 *    After this the trigger's recalculation will correctly equal
 *    manual_hours + current_session_hours.
 *
 * 2. INSERT TIME_ENTRY for the current timer session.
 *
 * 3. CLEAR the timer fields on the task row (timer_activo=false, timer_inicio=null).
 *    The DB trigger fires on step 2 and updates horas_ejecutadas automatically.
 */

import { supabase } from './supabase';

export interface StopTimerParams {
  taskId: string;
  /** ISO string from task.timer_inicio */
  timerInicio: string;
  /** Current value of task.horas_ejecutadas */
  currentHorasEjecutadas: number;
  userId: string;
  /** Optional work description (null = no comment / skipped) */
  description?: string | null;
}

export async function stopTimerAndSave(params: StopTimerParams): Promise<void> {
  const {
    taskId,
    timerInicio,
    currentHorasEjecutadas,
    userId,
    description = null,
  } = params;

  const startedAt = new Date(timerInicio);
  const endedAt   = new Date();

  // Guard: timer running less than 1 s — just clear it, don't create an entry
  if (endedAt.getTime() - startedAt.getTime() < 1000) {
    await supabase
      .from('apro_tasks')
      .update({ timer_activo: false, timer_inicio: null, updated_at: endedAt.toISOString() })
      .eq('id', taskId);
    return;
  }

  // ── 1. Reconcile manually-entered horas_ejecutadas ────────────────────────
  const { data: existingEntries } = await supabase
    .from('apro_time_entries')
    .select('duration_hours')
    .eq('task_id', taskId);

  const entriesSum = (existingEntries ?? []).reduce(
    (sum, e) => sum + (Number(e.duration_hours) ?? 0),
    0,
  );

  const manualHours = currentHorasEjecutadas - entriesSum;

  if (manualHours > 0.01) {
    // Place the synthetic entry in the window immediately before this session
    const syntheticEnd   = new Date(startedAt.getTime() - 1000); // 1 s before session
    const syntheticStart = new Date(syntheticEnd.getTime() - manualHours * 3_600_000);

    const { error: synthError } = await supabase
      .from('apro_time_entries')
      .insert({
        task_id:     taskId,
        user_id:     userId,
        started_at:  syntheticStart.toISOString(),
        ended_at:    syntheticEnd.toISOString(),
        description: 'Horas previas (registradas manualmente)',
        is_manual:   true,
      });

    if (synthError) {
      console.error('[Timer] Error creating synthetic entry for manual hours:', synthError);
      // Non-fatal: continue — at worst, manual hours are lost (same as before the fix)
    }
  }

  // ── 2. Insert time_entry for the actual timer session ─────────────────────
  const { error: entryError } = await supabase
    .from('apro_time_entries')
    .insert({
      task_id:     taskId,
      user_id:     userId,
      started_at:  startedAt.toISOString(),
      ended_at:    endedAt.toISOString(),
      description: description ?? null,
      is_manual:   false,
    });

  if (entryError) throw entryError;

  // ── 3. Clear timer on the task row ────────────────────────────────────────
  // The DB trigger fires after step 2 and recalculates horas_ejecutadas
  // as SUM(time_entries), so we do NOT touch horas_ejecutadas here.
  const { error: taskError } = await supabase
    .from('apro_tasks')
    .update({
      timer_activo: false,
      timer_inicio: null,
      updated_at:   endedAt.toISOString(),
    })
    .eq('id', taskId);

  if (taskError) throw taskError;
}
